import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Client, Track } from '../../models';
import { useAppSelector, useAppDispatch } from '../../redux';
import {
    loadTrack, deleteTrack, loadActiveTrack, createTrack, stopActiveTrack, updateChange,
} from '../../redux';
import { Icon, Tooltip, TrackClientPromptModal } from '..';
import { hasExplicitClient, resolveTrackClient } from '../../utils/resolveTrackClient';

interface ResumeTrackProps {
    track: Track;
    className?: string;
    hideRemove?: boolean;
}

function ResumeTrack(props: ResumeTrackProps) {
    const { track, className, hideRemove } = props;

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { projects } = useAppSelector(state => state.projects);
    const { active, loadingActive, stoppingActive, creating } = useAppSelector(state => state.tracks);
    const [promptOpen, setPromptOpen] = useState(false);

    const assignClientToLinkedChangeIfNeeded = (client: Client | AnyObj | string) => {
        const linked = track.change;
        if (!linked?._id || hasExplicitClient(linked))
            return;
        dispatch(updateChange({ _id: linked._id, client }, /*patch*/true, /*optimistic*/true));
    };

    const startWithClient = (client: Client | AnyObj | string) => {
        assignClientToLinkedChangeIfNeeded(client);
        dispatch(createTrack({
            title: track.title,
            change: track.change,
            project: track.project,
            client,
            billingMode: track.billingMode || 'hourly',
            billingDev: !!track.billingDev,
        }, {}, (err: Error | null) => {
            if (!err) {
                setPromptOpen(false);
                dispatch(loadActiveTrack());
            }
        }));
    };

    const start = (e: AnyObj) => {
        e.stopPropagation();
        const client = resolveTrackClient(track, projects);
        if (!client) {
            setPromptOpen(true);
            return;
        }
        startWithClient(client);
    };

    const remove = (e: AnyObj) => {
        e.stopPropagation();
        dispatch(deleteTrack(track._id));
    };

    const stop = (e: AnyObj) => {
        e.stopPropagation();
        dispatch(stopActiveTrack((err: Error | null) => {
            dispatch(loadTrack(active._id));
        }));
    };

    if (!track)
        return null;

    const isLoading = (
        loadingActive === 'pending' ||
        stoppingActive === 'pending' ||
        creating === 'pending'
    );

    const help = t('tracks.help.start_stop') || '';

    return (
        <div className={`group ${className || ''} cursor-pointer`}>
            {!active && (
                <div className="flex flex-row justify-end items-center space-x-2">
                    { !hideRemove && (
                        <Tooltip content={help} light>
                            <div onClick={remove}>
                                <Icon type={isLoading ? 'loading' : 'delete'} className="hidden group-hover:block" color="red-500" size={6} />
                            </div>
                        </Tooltip>
                    )}
                    <Tooltip content={help} light>
                        <div onClick={start}>
                            <Icon type={isLoading ? 'loading' : 'start'} color="blue-500" size={8} className="roup-hover:hidden" />
                        </div>
                    </Tooltip>
                </div>
            )}

            {active && active._id !== track._id && (
                <Tooltip content={help} light>
                    <div className="flex flex-row justify-end items-center space-x-2" onClick={start}>
                        <Icon type={isLoading ? 'loading' : 'start'} color="blue-500" size={8} />
                    </div>
                </Tooltip>
            )}

            {active && active._id === track._id && (
                <div className="flex flex-row justify-end items-center space-x-0">
                    <Icon type="running" className="group-hover:hidden" color="blue-500" size={8} />
                    <Tooltip content={help} light>
                        <div onClick={stop}>
                            <Icon type={isLoading ? 'loading' : 'stop'} className="hidden group-hover:block" color="red-500" size={8} />
                        </div>
                    </Tooltip>
                </div>
            )}
            <TrackClientPromptModal
                show={promptOpen}
                loading={creating === 'pending'}
                onClose={() => setPromptOpen(false)}
                onConfirm={startWithClient}
            />
        </div>
    );
}

export default ResumeTrack;
